import { Atom, atom, getDefaultStore } from 'jotai'
import { atomWithStorage, unwrap } from 'jotai/utils'
import { currentPubkeyAtom } from './session'
import { favoriteRelaysWithSetsAtom, favoriteFollowListsWithSetsAtom } from './lists'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { observe } from 'jotai-effect'

const store = getDefaultStore()

const MAX_RECENT_RELAY_FEEDS = 5

export type TFeed =
  | { pubkey: string }
  | { relay: string }
  | { relaySet: ResolvedSet<string> }
  | { followSet: ResolvedSet<string> }
  | { hardcodedFeed: number }

// persisted user override (null when nothing chosen yet)
const storedFeedAtom = atomWithStorage<TFeed | null>('s:feedInfo', null, undefined, {
  getOnInit: true
})

export const feedAtom: Atom<Promise<TFeed | undefined>> = atom(async (get) => {
  const stored = get(storedFeedAtom)
  if (stored) return stored

  const pubkey = get(currentPubkeyAtom)
  if (pubkey) return { pubkey }

  const favorites = await get(favoriteRelaysWithSetsAtom)
  if (!favorites || favorites.length === 0) {
    const hardcoded = window.hallway.universe.hardcodedFeeds
    if (hardcoded && hardcoded.length > 0) return { hardcodedFeed: 0 }
    return undefined
  }

  const firstItem = favorites[0]
  if (typeof firstItem === 'string') return { relay: firstItem }

  return { relaySet: firstItem }
})
export const unwrappedFeedAtom = unwrap(feedAtom)

export const includeRepliesAtom = atomWithStorage<boolean>(
  's:includeReplies',
  window.hallway.universe.includeRepliesDefault,
  undefined,
  { getOnInit: true }
)

export const groupedAtom = atomWithStorage<boolean>(
  's:grouped',
  window.hallway.universe.groupedDefault,
  undefined,
  { getOnInit: true }
)

export async function switchFeed(feed: TFeed) {
  store.set(storedFeedAtom, feed)
}

export const recentRelayFeedsAtom = atomWithStorage<(string | ResolvedSet<string>)[]>(
  's:recentRelayFeeds',
  [],
  undefined,
  { getOnInit: true }
)

export function trackRecentRelayFeed(ref: string | ResolvedSet<string>) {
  const current = store.get(recentRelayFeedsAtom)
  const idx =
    typeof ref === 'string'
      ? current.findIndex((it) => typeof it === 'string' && it === ref)
      : current.findIndex(
          (it) =>
            typeof it !== 'string' &&
            it.pointer.pubkey === ref.pointer.pubkey &&
            it.pointer.identifier === ref.pointer.identifier
        )

  let next
  if (idx !== -1) {
    next = [current[idx]]
    next.push(...current.slice(0, idx))
    next.push(...current.slice(idx + 1))
  }

  next = [ref, ...current.slice(0, MAX_RECENT_RELAY_FEEDS - 1)]
  store.set(recentRelayFeedsAtom, next)
}

// auto-update the feed when the follow sets update
observe((get, set) => {
  const pubkey = get.peek(currentPubkeyAtom)
  const feed = get.peek(feedAtom)
  const favoriteFollowSets = get(favoriteFollowListsWithSetsAtom)
  let stale = false

  Promise.all([feed, favoriteFollowSets]).then(([feed, favoriteFollowSets]) => {
    if (stale) return

    if (feed && 'followSet' in feed && feed.followSet.pointer.pubkey === pubkey) {
      const selectedFollowSet = favoriteFollowSets.find(
        (s) =>
          s.pointer.identifier === feed.followSet.pointer.identifier &&
          s.pointer.pubkey === feed.followSet.pointer.pubkey
      )
      if (selectedFollowSet) set(storedFeedAtom, { followSet: selectedFollowSet })
      else set(storedFeedAtom, null)
    }
  })

  return () => {
    stale = true
  }
})
